import { HeroBannerSlide as HeroBannerSlideType, SanityImage } from '@workspace/interfaces';
import { cn } from '@workspace/lib';
import { Picture } from '@workspace/ui/common';
import { HeroBannerVideo } from './HeroBannerVideo';
import { HeroBannerSize } from '../hero-banner.types';
import { getHeroBannerMinHeightClass } from '../hero-banner-util';

type HeroBannerImage = HeroBannerSlideType['image'] | SanityImage;

interface HeroBannerBackgroundProps {
  blockImage?: HeroBannerImage;
  contentPosition: 'left' | 'center' | 'right';
  isVideo?: boolean | null;
  videoUrl?: string | null;
  videoFile?: SanityImage | null;
  bynderVideo?: any;
  poster?: SanityImage | null;
  isActive?: boolean | null;
  size?: HeroBannerSize;
  shouldPreload?: boolean;
  showGradient?: boolean | null;
}

const getGradientClass = (contentPosition: 'left' | 'center' | 'right'): string => {
  switch (contentPosition) {
    case 'center':
      return 'hero-gradient-center';
    case 'right':
      return 'hero-gradient-right';
    default:
      return 'hero-gradient-left';
  }
};

export function HeroBannerBackground({
  blockImage,
  contentPosition,
  isVideo = false,
  videoUrl,
  videoFile,
  bynderVideo,
  poster,
  isActive = false,
  shouldPreload = false,
  size = 'medium',
  showGradient = true,
}: HeroBannerBackgroundProps) {
  const gradientClass = getGradientClass(contentPosition);
  const minHeightClass = getHeroBannerMinHeightClass(size);
  const shouldPrioritize = shouldPreload || (isActive ?? false);

  // Render video background if isVideo is true and a video source exists
  if (isVideo && (videoUrl || videoFile || bynderVideo)) {
    return (
      <div className={cn('absolute inset-0', minHeightClass)} suppressHydrationWarning>
        <HeroBannerVideo
          videoUrl={videoUrl ?? ''}
          videoFile={videoFile ?? null}
          bynderVideo={bynderVideo ?? null}
          poster={poster ?? null}
          isActive={isActive}
          shouldPreload={shouldPrioritize}
        />
        {showGradient !== false && (
          <div className={cn('absolute inset-0 pointer-events-none hero-gradient', gradientClass)} />
        )}
      </div>
    );
  }

  // Render image background if blockImage exists
  if (blockImage?.url) {
    return (
      <div className={cn('absolute inset-0', minHeightClass)} suppressHydrationWarning>
        <Picture
          image={blockImage}
          desktopWidth={1920}
          desktopHeight={692}
          tabletWidth={1024}
          tabletHeight={600}
          mobileWidth={390}
          mobileHeight={632}
          fill
          priority={shouldPrioritize}
        />

        {showGradient !== false && (
          <div className={cn('absolute inset-0 pointer-events-none hero-gradient', gradientClass)} />
        )}
      </div>
    );
  }

  // Render gradient only if no image or video
  return (
    <div
      className={cn('absolute inset-0 pointer-events-none hero-gradient', gradientClass, minHeightClass)}
      suppressHydrationWarning
    />
  );
}
